import { useState, useCallback } from 'react';
import { ApiClient } from '@/services/api';
import { toastError, toastSuccess } from '@/utils/toastHelper';

export interface PasswordChangeRequest {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export interface PasswordChangeResponse {
  success: boolean;
  message: string;
}

export interface UseChangePasswordReturn {
  loading: boolean;
  error: string | null;
  changePassword: (params: PasswordChangeRequest) => Promise<boolean>;
  clearError: () => void;
}

export const useChangePassword = (): UseChangePasswordReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changePassword = useCallback(
    async (params: PasswordChangeRequest): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        // Validación cliente-side (antes de enviar al servidor)
        if (!params.current_password || !params.new_password || !params.confirm_password) {
          throw new Error('Todos los campos son requeridos');
        }

        if (params.new_password !== params.confirm_password) {
          throw new Error('Las contraseñas no coinciden');
        }

        if (params.new_password.length < 8) {
          throw new Error('La contraseña debe tener al menos 8 caracteres');
        }

        if (!/[A-Z]/.test(params.new_password)) {
          throw new Error('La contraseña debe contener al menos una mayúscula');
        }

        if (!/[a-z]/.test(params.new_password)) {
          throw new Error('La contraseña debe contener al menos una minúscula');
        }

        if (!/[0-9]/.test(params.new_password)) {
          throw new Error('La contraseña debe contener al menos un número');
        }

        if (!/[!@#$%^&*()_+\-=\[\]{};:,.<>?]/.test(params.new_password)) {
          throw new Error('La contraseña debe contener al menos un carácter especial (!@#$%^&*...)');
        }

        // Llamar al endpoint con FormData (patrón p_rendicion)
        const formData = new FormData();
        formData.append('current_password', params.current_password);
        formData.append('new_password', params.new_password);
        formData.append('confirm_password', params.confirm_password);

        const response = await ApiClient.post<PasswordChangeResponse>(
          '/api/auth/change-password',
          formData
        );

        toastSuccess('Contraseña actualizada correctamente');
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error al cambiar la contraseña';
        setError(errorMessage);
        toastError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    changePassword,
    clearError
  };
};
